import { Box, Button as HeaderCustomButton, Stack } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { useLocation } from 'react-router';
import { ChevronDown } from '@sweep-io/sweep-design/dist/icons';
import { DashboardSubmenu } from './DashboardSubmenu';
import { HOVER_COLOR, ACTIVE_COLOR } from './utils/colors';
import { MenuColumn } from './utils/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { designSystemElevations } from '../../../appTheme/appTheme';
import { handleNavigationWithModifiers } from './utils/navigationUtils';

interface HeaderButtonProps {
  label: string;
  onClick: () => void;
  route: string;
  Icon: any;
  menuColumns?: MenuColumn[];
}

export const HeaderButton = ({ label, route, onClick, Icon, menuColumns }: HeaderButtonProps) => {
  const location = useLocation();
  const [isSubmenuVisible, setIsSubmenuVisible] = useState(false);
  const [isSubmenuClicked, setIsSubmenuClicked] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && (!event.target || !ref.current.contains(event.target as HTMLElement))) {
      setIsSubmenuClicked(false);
      setIsSubmenuVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const checkDoesChildHaveRoutes = useCallback(
    (columns: MenuColumn[]): boolean => {
      if (!columns) {
        return false;
      }
      for (const column of columns) {
        for (const item of column.menuItems) {
          if (location.pathname.includes(item.route)) {
            return true;
          }
          if (item.subMenuItems && item.subMenuId) {
            const hasMatch = checkDoesChildHaveRoutes([
              {
                columnId: item.subMenuId,
                columnLabel: item.subMenuId,
                menuItems: item.subMenuItems,
              },
            ]);
            if (hasMatch) {
              return true;
            }
          }
        }
      }
      return false;
    },
    [location.pathname],
  );

  const handleMouseEnter = useCallback(() => {
    if (menuColumns) {
      setIsSubmenuVisible(true);
    }
  }, [menuColumns]);

  const handleMouseLeave = useCallback(() => {
    if (menuColumns) {
      setTimeout(() => {
        setIsSubmenuVisible(false);
      }, 150);
    }
  }, [menuColumns]);

  const isChildPath = menuColumns ? checkDoesChildHaveRoutes(menuColumns) : false;
  const isActive = location.pathname.includes(route) || isChildPath;

  return (
    <Stack
      sx={{ m: '8px 0px' }}
      onMouseEnter={() => {
        if (!isSubmenuClicked) {
          handleMouseEnter();
        }
      }}
      onMouseLeave={() => {
        if (!isSubmenuClicked) {
          handleMouseLeave();
        }
      }}
      ref={ref}
    >
      <HeaderCustomButton
        variant="contained"
        color="primary"
        sx={{
          background: isActive ? ACTIVE_COLOR : isSubmenuClicked ? HOVER_COLOR : colors.white,
          color: colors.grey[900],
          whiteSpace: 'nowrap',
          p: '8px 12px',
          borderRadius: '8px',
          '&:hover': {
            background: isActive ? ACTIVE_COLOR : HOVER_COLOR,
          },
          height: '40px',
        }}
        onClick={(event) => {
          if (!menuColumns) {
            handleNavigationWithModifiers(event, route, onClick);
            event.stopPropagation();
          } else {
            setIsSubmenuClicked(true);
          }
        }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          {Icon} <Typography variant="body">{label}</Typography>
        </Stack>
        {!!menuColumns && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ChevronDown size="small" />
          </Box>
        )}
        {menuColumns && (
          <Stack
            sx={{
              borderRadius: '8px',
              position: 'absolute',
              top: '44px',
              zIndex: 12,
              background: colors.white,
              boxShadow: designSystemElevations.Elevation7,
              display: isSubmenuVisible ? 'block' : 'none',
              textAlign: 'left',
              left: 0,
              // Add invisible bridge above the menu to maintain hover state
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-5px',
                left: 0,
                right: 0,
                height: '5px',
                background: 'transparent',
              },
            }}
          >
            <DashboardSubmenu
              menuColumns={menuColumns}
              onSelect={() => {
                setIsSubmenuClicked(false);
              }}
            />
          </Stack>
        )}
      </HeaderCustomButton>
    </Stack>
  );
};
